import React, { useState, useEffect } from 'react';
import TotInventory from '../components/Dashboard/TotInventory.jsx';
import HighestMenuItem from '../components/Dashboard/HighestMenuItem';
import LowestMenuItem from '../components/Dashboard/LowestMenuItem';
import ExpensiveIngredient from '../components/Dashboard/ExpensiveIngredient';
import DateFilter from '../components/Menu/DateFilter';
import VendorTable from '../components/Dashboard/VendorTable.jsx';
import CategoryTable from '../components/Dashboard/CategoryTable.jsx';
import VendorLineGraph from '../components/Dashboard/VendorLineGraph.jsx';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PageHeader from '../components/layout/PageHeader';
import { tokens } from '../theme';

const panelSx = {
  backgroundColor: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 2,
  overflow: 'hidden',
  height: '100%',
};

const Dashboard = ({ reload }) => {
  const dateOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
  const [load, setLoad] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showLoad, setShowLoad] = useState(false);
  const [reloadDash, setReloadDash] = useState(reload);

  useEffect(() => {
    if (startDate === null && endDate === null) {
      initializeDates();
    }
    setLoad(false);
  }, [startDate, endDate]);

  useEffect(() => {
    setReloadDash(reload);
  }, [reload]);

  const initializeDates = () => {
    let todayDate = new Date();
    const lastWeekDate = new Date(todayDate);
    lastWeekDate.setDate(todayDate.getDate() - 30);
    todayDate = todayDate.toLocaleDateString('en-US', dateOptions);
    const dateOne = lastWeekDate.toLocaleDateString('en-US', dateOptions);
    setStartDate(dateOne);
    setEndDate(todayDate);
  };

  const dateClicked = async (start, end) => {
    setShowLoad(true);
    setStartDate(start);
    setEndDate(end);
    setReloadDash(true);
    setTimeout(() => setReloadDash(false), 200);
  };

  if (load) {
    return (
      <Box className="pg-spin">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeader
        title="Analytics"
        subtitle="Spend, margins, and ingredient cost for the selected range"
        actions={
          <DateFilter
            ingredient="null"
            dates={dateClicked}
            style="dashboard"
            defaultDays={30}
            showLoading={showLoad}
          />
        }
      />

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            lg: 'repeat(4, 1fr)',
          },
          mb: 2.5,
        }}
      >
        <TotInventory startDate={startDate} endDate={endDate} reload={reloadDash} showLoad={() => setShowLoad(false)} />
        <HighestMenuItem reload={reloadDash} />
        <LowestMenuItem reload={reloadDash} />
        <ExpensiveIngredient startDate={startDate} endDate={endDate} reload={reloadDash} showLoad={() => setShowLoad(false)} />
      </Box>

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          mb: 2.5,
          minHeight: { lg: 420 },
        }}
      >
        <Box sx={panelSx}>
          <VendorTable startDate={startDate} endDate={endDate} reload={reloadDash} />
        </Box>
        <Box sx={panelSx}>
          <CategoryTable startDate={startDate} endDate={endDate} reload={reloadDash} />
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 } }}>
        <Box sx={{ ...panelSx, p: 2, minHeight: 380 }}>
          <VendorLineGraph startDate={startDate} endDate={endDate} reload={reloadDash} />
        </Box>
        <Typography variant="caption" sx={{ color: tokens.inkMuted, display: 'block', mt: 1.5, px: 0.5 }}>
          COGs & margin trends over time — fuller analytics coming next
        </Typography>
      </Box>
    </Box>
  );
};

export default Dashboard;
